import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect } from 'react'
import { ClockIcon, MapIcon, ArrowUpIcon, ChartBarIcon, HeartIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline'

import ContentFrame from 'components/Frame/ContentFrame'
import BrandColorButton from 'components/Buttons/BrandColorButton'

// Configuration for which metrics should display totals
const METRIC_CONFIG = {
    pace: { showTotal: false },
    heartRate: { showTotal: false },
    // Add more configurations as needed
    // Default behavior: showTotal: true for all other metrics (unless explicitly set to false)
}

export default function WearablesStats({ userId }) {

    const [wearableStats, setWearableStats] = useState(null)
    const [loading, setLoading] = useState(true)
    const [windowSize, setWindowSize] = useState(7)

    const windowSizeOptions = [
        { value: 7, label: '7 Days' },
        { value: 14, label: '14 Days' },
        { value: 30, label: '30 Days' },
        { value: 60, label: '60 Days' }
    ]

    const GetWearableStatsFromBackend = async () => {
        setLoading(true)
        
        const jsonBody = {
            userId: userId,
            windowSize: windowSize
        }

        try {
            const result = await WhichWeightAPI('CORE', 'GET', '/wearable_stats', jsonBody)
            
            if (result.data.status === 'success' && result.data.data.status === 'success') {
                setWearableStats(result.data.data.data)
            } else {
                setWearableStats(null)
            }
        } catch (error) {
            console.error('Error fetching wearable stats:', error)
            setWearableStats(null)
        } finally {
            setLoading(false)
        }
    }

    const handleRecalculate = async () => {
        try {
            await WhichWeightAPI('CORE', 'POST', `/wearable_stats_calculate?userId=${userId}`)
            // Refresh the data after recalculation
            GetWearableStatsFromBackend()
        } catch (error) {
            console.error('Error recalculating wearable stats:', error)
        }
    }

    useEffect(() => { GetWearableStatsFromBackend() }, [userId, windowSize])

    const formatValue = (value, unit) => {
        if (value === null || value === undefined) return 'N/A'
        
        if (typeof value === 'number') {
            if (unit === 'seconds') {
                const hours = Math.floor(value / 3600)
                const minutes = Math.floor((value % 3600) / 60)
                const seconds = Math.round(value % 60)
                if (hours > 0) {
                    return `${hours}h ${minutes}m ${seconds}s`
                } else if (minutes > 0) {
                    return `${minutes}m ${seconds}s`
                } else {
                    return `${seconds}s`
                }
            }
            if (unit === 'meters') {
                if (value >= 1000) {
                    return `${(value / 1000).toFixed(1)} km`
                } else {
                    return `${value.toFixed(0)} m`
                }
            }
            if (unit === 'm/s') {
                // Convert m/s to min/km for running pace
                const paceMinPerKm = 1000 / value / 60
                return `${paceMinPerKm.toFixed(1)} min/km`
            }
            if (unit === 'bpm') {
                return `${value.toFixed(0)} bpm`
            }
            if (unit === 'trimp') {
                return `${value.toFixed(0)}`
            }
            return value.toFixed(2)
        }
        
        return value
    }

    const getStatIcon = (statKey) => {
        switch (statKey) {
            case 'duration':
                return <ClockIcon className="h-5 w-5 text-blue-500" />
            case 'distance':
                return <MapIcon className="h-5 w-5 text-green-500" />
            case 'verticalGain':
                return <ArrowUpIcon className="h-5 w-5 text-purple-500" />
            case 'pace':
                return <ChartBarIcon className="h-5 w-5 text-orange-500" />
            case 'heartRate':
                return <HeartIcon className="h-5 w-5 text-red-500" />
            case 'averageAltitude':
            case 'minimumAltitude':
            case 'maximumAltitude':
                return <ArrowTrendingUpIcon className="h-5 w-5 text-indigo-500" />
            case 'trimp':
                return <ChartBarIcon className="h-5 w-5 text-yellow-500" />
            default:
                return <ChartBarIcon className="h-5 w-5 text-gray-500" />
        }
    }

    const getStatUnit = (statKey) => {
        switch (statKey) {
            case 'duration':
                return 'seconds'
            case 'distance':
                return 'meters'
            case 'verticalGain':
                return 'meters'
            case 'pace':
                return 'm/s'
            case 'heartRate':
                return 'bpm'
            case 'averageAltitude':
            case 'minimumAltitude':
            case 'maximumAltitude':
                return 'meters'
            case 'trimp':
                return 'trimp'
            default:
                return ''
        }
    }

    const getStatName = (statKey) => {
        switch (statKey) {
            case 'duration':
                return 'Duration'
            case 'distance':
                return 'Distance'
            case 'verticalGain':
                return 'Vertical Gain'
            case 'pace':
                return 'Pace'
            case 'heartRate':
                return 'Heart Rate'
            case 'averageAltitude':
                return 'Average Altitude'
            case 'minimumAltitude':
                return 'Minimum Altitude'
            case 'maximumAltitude':
                return 'Maximum Altitude'
            case 'trimp':
                return 'TRIMP'
            default:
                return statKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
        }
    }

    if (loading) {
        return (
            <div className='w-full h-80 bg-gray-200 rounded-lg animate-pulse' />
        )
    }

    if (!wearableStats) {
        return (
            <ContentFrame>
                <div className="w-full space-y-4">

                    {/* Window Size Selector */}
                    <div className="p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Time Window</h3>

                            <div className="w-32">
                                <BrandColorButton
                                    text="Recalculate"
                                    action={handleRecalculate}
                                />
                            </div>
                        </div>
                        <div className="flex space-x-4">
                            {windowSizeOptions.map((option) => (
                                <label key={option.value} className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="windowSize"
                                        value={option.value}
                                        checked={windowSize === option.value}
                                        onChange={(e) => setWindowSize(parseInt(e.target.value))}
                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">{option.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="p-4">
                        <div className="text-center">
                            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No Wearable Data</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                No wearable statistics found for this athlete in the last {windowSize} days.
                            </p>
                            <p className="mt-2 text-sm text-gray-400">
                                Try selecting a different time window above to see if data exists for other periods.
                            </p>
                        </div>
                    </div>
                </div>
            </ContentFrame>
        )
    }

    // Filter out non-stat fields and empty stat objects
    const statsToDisplay = Object.entries(wearableStats).filter(([key, value]) => {
        const nonStatFields = ['id', 'userId', 'createdTimestamp', 'updatedTimestamp', 'windowSize']
        return !nonStatFields.includes(key) && 
               typeof value === 'object' && 
               value !== null && 
               Object.keys(value).length > 0 &&
               value.hasOwnProperty('avg') // Ensure it's a valid stat object
    })

    return (
        <ContentFrame>
            <div className="w-full space-y-4">

                {/* Window Size Selector */}
                <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Time Window</h3>

                        <div className="w-32">
                            <BrandColorButton
                                text="Recalculate"
                                action={handleRecalculate}
                            />
                        </div>
                    </div>
                    <div className="flex space-x-4">
                        {windowSizeOptions.map((option) => (
                            <label key={option.value} className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="windowSize"
                                    value={option.value}
                                    checked={windowSize === option.value}
                                    onChange={(e) => setWindowSize(parseInt(e.target.value))}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                />
                                <span className="ml-2 text-sm font-medium text-gray-700">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {statsToDisplay.map(([key, stat]) => (
                        <div key={key} className="bg-white rounded-lg shadow border border-stone-100 p-4">
                            <div className="flex items-center">
                                {getStatIcon(key)}
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-500">{getStatName(key)}</p>
                                    <p className="text-lg font-semibold text-gray-900">
                                        {formatValue(stat.avg, getStatUnit(key))}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Detailed Table */}
                <div className="bg-white rounded-lg shadow border border-stone-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Wearable Statistics Details</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Based on {statsToDisplay[0]?.[1]?.count || 0} data points over {windowSize} days
                        </p>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Metric
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Average
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Std Dev
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Count
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Earliest Record
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Latest Record
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {statsToDisplay.map(([key, stat]) => (
                                    <tr key={key} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {getStatIcon(key)}
                                                <span className="ml-2 font-medium text-gray-900">{getStatName(key)}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatValue(stat.avg, getStatUnit(key))}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {METRIC_CONFIG[key]?.showTotal === false ? 'N/A' : formatValue(stat.sum, getStatUnit(key))}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatValue(stat.std, getStatUnit(key))}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {stat.count}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {stat.earliest_record_time ? 
                                                new Date(stat.earliest_record_time).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                }) : 'N/A'
                                            }
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {stat.latest_record_time ? 
                                                new Date(stat.latest_record_time).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                }) : 'N/A'
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Data Info */}
                {wearableStats.createdTimestamp && (
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600">
                            <p><strong>Data Created:</strong> {new Date(wearableStats.createdTimestamp).toLocaleString()}</p>
                            {wearableStats.updatedTimestamp && (
                                <p><strong>Last Updated:</strong> {new Date(wearableStats.updatedTimestamp).toLocaleString()}</p>
                            )}
                            <p><strong>Window Size:</strong> {wearableStats.windowSize} days</p>
                        </div>
                    </div>
                )}
            </div>
        </ContentFrame>
    )
} 